// src/utils/xlsx.tsx
//
// SheetJS (xlsx, ≈ 470 kB nezkomprimovaně – víc než polovina dřívějšího
// hlavního bundlu) není v hlavním bundlu (úklid a rychlost, 12. 10. 2026).
// Stahuje se předem, jakmile se otevře obrazovka s exportem nebo importem
// (useXlsx), a drží se v modulu – samotné kliknutí na export pak soubor
// zapíše SYNCHRONNĚ (xlsxNow), ještě v gestu uživatele: Safari na iPhonu
// (majitel exportuje z telefonu) stažení spuštěné až po await v kliknutí
// nemusí povolit. Dokud se knihovna nestáhne, je tlačítko exportu zakázané
// s textem „Připravuji export…“ (XlsxNotReady); offline se zkusí znovu po
// návratu připojení.

import React, { useEffect, useState } from "react";
import { reportWarning } from "./reportError";

export type XlsxModule = typeof import("xlsx");

let loaded: XlsxModule | null = null;
let pending: Promise<XlsxModule> | null = null;

/** Stáhne SheetJS (jednou; po chybě další volání zkusí znovu). */
export function loadXlsx(): Promise<XlsxModule> {
  if (loaded) return Promise.resolve(loaded);
  if (!pending) {
    pending = import(/* webpackChunkName: "xlsx" */ "xlsx").then(
      (mod) => {
        loaded = mod;
        pending = null;
        return mod;
      },
      (err: unknown) => {
        pending = null;
        throw err;
      }
    );
  }
  return pending;
}

/** Už stažený SheetJS pro synchronní export v kliknutí; null = ještě ne. */
export function xlsxNow(): XlsxModule | null {
  return loaded;
}

export interface XlsxState {
  /** Stažená knihovna – export ji použije synchronně. */
  xlsx: XlsxModule | null;
  /** Stažení selhalo (offline); po návratu připojení se zkusí znovu. */
  failed: boolean;
}

/**
 * Obrazovka s exportem/importem: stáhne SheetJS hned po otevření a vrátí
 * stav pro tlačítko (zakázané, dokud xlsx není).
 */
export function useXlsx(): XlsxState {
  const [state, setState] = useState<XlsxState>(() => ({ xlsx: loaded, failed: false }));
  const ready = !!state.xlsx;
  useEffect(() => {
    if (ready) return undefined;
    let alive = true;
    const attempt = () => {
      loadXlsx().then(
        (xlsx) => {
          if (alive) setState({ xlsx, failed: false });
        },
        (err: unknown) => {
          reportWarning("xlsx.load", err);
          if (alive) setState({ xlsx: null, failed: true });
        }
      );
    };
    attempt();
    window.addEventListener("online", attempt);
    return () => {
      alive = false;
      window.removeEventListener("online", attempt);
    };
  }, [ready]);
  return state;
}

export const XLSX_PREPARING_TEXT = "Připravuji export…";
export const XLSX_FAILED_TITLE =
  "Export se nepodařilo připravit – zkontrolujte připojení k internetu, po připojení se připraví sám.";

/** Obsah tlačítka exportu, dokud SheetJS není stažený. */
export const XlsxNotReady: React.FC<{ failed: boolean }> = ({ failed }) => (
  <>
    {failed ? (
      <i className="bi bi-wifi-off me-1" aria-hidden="true" />
    ) : (
      <span className="spinner-border spinner-border-sm me-1" aria-hidden="true" />
    )}
    {XLSX_PREPARING_TEXT}
  </>
);
